import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import moment from 'moment';

import { MoreVertIconContrast } from '../../../icons/MoreVertIconContrast';
import { TYPES, useShowModal } from '../../../actions/modal';
import { cancelDeleteReportData, deleteReportData } from '../../../actions/reports';
import { ListItem, ListItemSecondaryAction, ListItemText } from '../../material-ui/List';
import { IconButton } from '../../material-ui/IconButton';
import { Menu, MenuItem } from '../../material-ui/Menu';
import { useStyles } from './useStyles';
import { useAnchorEl } from '../../hooks/useAnchorEl';
import { useDisable } from '../hooks/useDisable';
import { getLatestClosedDate } from './tools/getLatestClosedDate';

export const ReportListItem = memo(({ report }) => {
    const {
        createdDate,
        feedbacks = [],
        flightDate,
        flightName,
        id,
        internalCode,
        isEdited,
        isNew,
        isRemoved,
        number,
        safetyGroupTypeName,
        safetyTypeName,
        statusDate,
        statusType,
    } = report;

    const showModal = useShowModal();
    const dispatch = useDispatch();
    const classes = useStyles();
    const [anchorEl, handleOpenMenu, handleClose] = useAnchorEl();
    const { t: translate } = useTranslation();
    const { disableEdit, disableDelete } = useDisable({ feedbacks, internalCode, statusDate });
    const primary = `${flightName} (${moment(flightDate).format('DD.MM.YYYY')}) ${translate('reports.report')} ${number || '–'} (${moment
        .utc(createdDate)
        .format('DD.MM.YYYY HH:mm')})`;
    const secondaryText = `${safetyGroupTypeName} / ${safetyTypeName}`;

    const latestClosedDate = getLatestClosedDate(feedbacks);

    const statusText = latestClosedDate
        ? `${translate('closed')} (${moment.utc(latestClosedDate).format('DD.MM.YYYY HH:mm')})`
        : statusType
            ? `${statusType} (${moment.utc(statusDate).format('DD.MM.YYYY HH:mm')})`
            : '';
    const className = isRemoved
        ? classes.listItemRemoved
        : disableEdit || disableDelete
            ? classes.listItemDisabled
            : isEdited || isNew
                ? classes.listItemEdited
                : {};

    const handleClick = React.useCallback(() => {
        if (!isRemoved && !disableEdit && !disableDelete) {
            showModal(TYPES.REPORT, {
                report,
                title: translate('modal.editReport'),
            });
            handleClose();
        }
    }, [isRemoved, disableEdit, disableDelete, report]);

    const handleDelete = React.useCallback(() => {
        dispatch(deleteReportData(report.id));
        handleClose();
    }, [report.id]);

    const handleCancelDelete = React.useCallback(() => {
        dispatch(cancelDeleteReportData(report.id));
        handleClose();
    }, [report.id]);

    const handleDetailClick = React.useCallback(() => {
        showModal(TYPES.REPORT_DETAIL, { report });
        handleClose();
    }, [report]);

    return (
        <>
            <ListItem
                id={id}
                disabled={isRemoved || disableEdit}
                button={!isRemoved && !disableEdit && !disableDelete}
                className={className}
                onClick={handleClick}
            >
                <ListItemText
                    primary={
                        <span className={classes.reportWrapper}>
                            <span className={classes.report}>{primary}</span>
                            <span className={classes.status}>{statusText}</span>
                        </span>
                    }
                    secondary={<span className={classes.secondaryText}>{secondaryText}</span>}
                />
                <ListItemSecondaryAction>
                    <IconButton style={{ padding: '9px' }} onClick={handleOpenMenu}>
                        <MoreVertIconContrast />
                    </IconButton>
                </ListItemSecondaryAction>
            </ListItem>
            <Menu id="simple-menu" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
                {!isRemoved && <MenuItem onClick={handleDetailClick}>{translate('showDetails')}</MenuItem>}
                {!(isRemoved || disableDelete) && <MenuItem onClick={handleDelete}>{translate('reports.deleteReport')}</MenuItem>}
                {isRemoved && <MenuItem onClick={handleCancelDelete}>{translate('reports.cancelDeletion')}</MenuItem>}
            </Menu>
        </>
    );
});
